import { useEffect, useRef } from 'react';
import { withBasePath } from '../utils/paths.js';

/**
 * Full-screen photo viewer — mirrors website site-lightbox markup + script.js behaviour.
 */
export default function SiteLightbox({
  open,
  index,
  photos,
  onClose,
  onPrev,
  onNext,
  label = 'Photo viewer',
}) {
  const closeRef = useRef(null);
  const lastFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    lastFocusRef.current = document.activeElement;
    document.body.classList.add('is-lightbox-open');
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('is-lightbox-open');
      const prev = lastFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, onClose, onNext, onPrev]);

  if (!open || !photos.length) return null;

  const photo = photos[index] || photos[0];
  const counter = `${index + 1} / ${photos.length}`;

  return (
    <div
      id="siteLightbox"
      className="site-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className="site-lightbox__backdrop" tabIndex={-1} onClick={onClose} />
      <div className="site-lightbox__panel">
        <button
          ref={closeRef}
          type="button"
          className="site-lightbox__close"
          aria-label="Close gallery"
          onClick={onClose}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <button type="button" className="site-lightbox__nav site-lightbox__nav--prev" aria-label="Previous photo" onClick={onPrev}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <figure className="site-lightbox__figure">
          <img className="site-lightbox__img" src={withBasePath(photo.src)} alt={photo.alt || ''} />
          <figcaption className="site-lightbox__caption">
            <span className="site-lightbox__counter">{counter}</span>
          </figcaption>
        </figure>
        <button type="button" className="site-lightbox__nav site-lightbox__nav--next" aria-label="Next photo" onClick={onNext}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
